/*
 * 사용자 몰 — 납품처 설정 정본 (배포 시점 값)
 *
 * 🔴 **몰 설정은 두 곳입니다** (2026-09-23 스킨 체계 · `docs/base/11-스킨-체계.md` §1).
 *    - **이 파일** — 사이트당 한 번 정해지는 값: 배포 스위치(`COMING_SOON` · `SEARCH_INDEX` ·
 *      `SITE_URL`)와 **법인 정보**(`COMPANY` · `CONTACT_*` · `SUPPORT_HOURS`), 화면 문구.
 *    - **DB(`site_settings` · 관리자가 인트라넷에서)** — 스킨 · 브랜드색 · 모서리 · **서비스명 ·
 *      태그라인 · 로고** · 약관 본문. 몰은 `GET /site` 로 받아 `useSite()` 로 씁니다.
 *      재배포 없이 바뀌는 값이라 여기 두지 않습니다.
 *    화면과 메타데이터는 여기서 읽기만 합니다.
 *
 *    🔄 **2026-09-12 — 랜딩(`apps/landing`)이 폐기되고 몰이 apex 도메인을 받았습니다**
 *       (오너 결정). 이 파일 아래 몇몇 주석은 "랜딩과 같은 구조" 를 근거로 들었는데,
 *       이제 몰이 유일한 프런트라 그 비교 대상이 사라졌습니다.
 *
 *    🔑 **service 브랜치가 고쳐도 되는 파일**입니다. 반대로 `page.tsx`·`layout.tsx` 를
 *       브랜치에서 고치기 시작하면, 베이스가 그 파일을 개선할 때마다 머지 충돌이
 *       납니다. 값이 모자라면 화면을 고치지 말고 **여기에 값을 늘리세요.**
 *
 * 🔴 이 앱은 그대로 **Hybrid Wrapper 로 감싸져 앱 마켓에 올라갑니다.** 스토어 등록
 *    정보와 여기 이름이 어긋나면 심사에서 걸립니다.
 */

/**
 * 🔴 **이 몰의 정본 주소입니다** (AGENTS.md §5-2 — "몰은 별개의 Next.js 앱이고
 *    자기 주소를 `NEXT_PUBLIC_SITE_URL` 로 갖습니다").
 *
 * 🔴 **빌드 시점에 박힙니다.** 페이지가 정적으로 미리 그려지므로 canonical·og:url
 *    이 산출물에 그대로 굳습니다 — 런타임 환경변수로는 못 바꿉니다. 그래서 환경마다
 *    이미지를 따로 굽고(`apps/mall/Dockerfile` 의 build arg), 그 값이 여기 들어옵니다.
 *
 * 🔑 **기본값이 dev 인 이유** — 값을 빠뜨렸을 때 어느 쪽으로 틀리는 게 나은지의
 *    문제입니다. 기본을 운영 주소로 두면 dev·test 가 자기를 운영 도메인이라고
 *    주장하게 되고, 되돌리는 데 몇 주가 걸립니다. 반대로 운영에서 빠뜨리면 링크가
 *    dev 를 가리킬 뿐이고 한 번 더 배포하면 돌아옵니다.
 *
 * 🔄 **2026-09-12 — apex 로 옮겼습니다.** 랜딩이 있던 시절에는 몰이 `mall.<도메인>`
 *    이라 자리표시도 `mall.dev.example.com` 이었습니다. 랜딩 폐기로 몰이 apex 를
 *    받으면서 자리표시에서도 `mall.` 접두사를 뗐습니다.
 *
 * ⚠️ **색인 스위치(`SEARCH_INDEX`)·법정 표시 가드는 여기 없습니다.**
 *    몰은 화면이 다 있어도 `COMING_SOON` 으로 닫혀 있고, 색인은 아래 스위치 하나가 막습니다.
 *    몰이 실제로 문을 열 때 그 가드를 이쪽에도 세우는 것이 맞습니다.
 */
export const SITE_URL = (
  process.env.NEXT_PUBLIC_SITE_URL ?? "https://dev.example.com"
).replace(/\/+$/, "");

/*
 * 🔑 **서비스명·태그라인의 «기본값» 입니다 — 「티켓세상」** (오너 지시 2026-09-13).
 *
 * 🔴 **화면은 이 값을 직접 읽지 않습니다** (2026-09-23). 서비스명은 관리자가 인트라넷
 *    «몰 설정» 에서 정하고(`site_settings.brand`), 화면은 `useSite().brand.siteName` 을
 *    씁니다. 이 값은 DB 가 비었거나 `GET /site` 를 못 받았을 때만 나갑니다
 *    (`_lib/siteConfig.ts` 의 `DEFAULT_BRAND`). 그래서 service 브랜치가 이 줄을 고칠 일도
 *    거의 없습니다 — 이름을 바꾸려면 관리자 화면에서 바꾸세요.
 *    띄어쓰기 없이 한 단어로 씁니다 — 로고 워드마크·도메인·앱 마켓 등록명이 이 표기를 따릅니다.
 */
export const SITE_NAME = "티켓세상";
export const SITE_TAGLINE = "모바일 상품권";

/** 문의 창구. 🔑 오픈 전에는 이 주소가 사실상 유일한 접점입니다. */
export const CONTACT_EMAIL = "CHANGE_ME@example.com";

/** 설정 전 주소는 실제 문의 버튼으로 노출하지 않습니다. CONTACT_EMAIL이 설정 정본입니다. */
export const SUPPORT_EMAIL = CONTACT_EMAIL.includes("CHANGE_ME") ? "" : CONTACT_EMAIL;

/**
 * 푸터 법정 표시. 납품처별 실값은 이 파일 한 곳에서만 바꿉니다.
 *
 * 🔴 **전자상거래법 제10조 표시 의무 항목입니다.** 몰 재디자인(2026-09-12)의
 *    새 푸터가 이 항목을 전부 그립니다 — `docs/design/mall-redesign/README.md`
 *    §11 검수표. `address` · `privacyOfficer` 는 2026-09-12 에 늘었습니다.
 *
 * 🆕 **`hostingProvider` · `escrowProvider` · `escrowUrl`** (2026-09-23 · `docs/base/11` §5-3).
 *    전자상거래법 시행령 제10조(호스팅서비스 제공자의 상호)와 법 제24조(구매안전서비스 —
 *    결제대금예치·소비자피해보상보험)의 표시 항목인데, 종전 푸터에 빠져 있었습니다.
 *    `escrowUrl` 은 그 서비스의 **가입 사실 확인 주소**(PG 사가 줍니다)입니다. 🔑 비었으면 그리지
 *    않습니다(`isFilled`) — PG 계약 전에는 구매안전서비스 항목이 없습니다.
 */
export const COMPANY = {
  name: "주식회사 CHANGE_ME",
  nameEn: "CHANGE_ME Co., Ltd.",
  ceo: "CHANGE_ME",
  bizNo: "CHANGE_ME",
  mailOrderNo: "제0000-CHANGE_ME-0000호",
  /** 사업장 주소. */
  address: "CHANGE_ME",
  /** 개인정보보호책임자. */
  privacyOfficer: "CHANGE_ME",
  /** 호스팅서비스 제공자 (서버를 빌려 주는 회사의 상호). */
  hostingProvider: "CHANGE_ME",
  /** 구매안전서비스 제공자 (결제대금예치 · 보험). PG 계약 뒤 채웁니다. */
  escrowProvider: "",
  /** 구매안전서비스 가입 사실 확인 주소 (https://…). 비면 확인 링크를 그리지 않습니다. */
  escrowUrl: "",
} as const;

/**
 * 🚧 오픈 준비 중인가.
 *
 * 🔴 `true` 인 동안 `layout.tsx` 가 `noindex` 를 보냅니다 — 준비 중 화면이 검색에
 *    잡히면 **정식 오픈 뒤에도 그 화면이 한동안 결과에 남습니다.**
 *    오픈할 때 아래 기본값을 `false` 로 바꾸세요 (service 브랜치에서).
 *    🔑 검색 노출은 별개 스위치(`SEARCH_INDEX`)입니다 — 테스트 공유로 열 때는 그대로 둡니다.
 *
 * 🔑 **개발 스택은 환경변수로 엽니다** (2026-09-07 · 오너 결정). `main` 의 기본값은
 *    닫힘이어야 하는데(새 사이트가 열린 채 뜨면 안 됩니다) 개발 도메인에서는 늘
 *    몰이 보여야 해서, `infra/docker/dev/compose.yml` 이 `NEXT_PUBLIC_COMING_SOON=0`
 *    을 넘깁니다. 운영 이미지에는 그 변수가 없으므로 아래 기본값이 그대로입니다.
 *    ⚠️ `NEXT_PUBLIC_*` 라 운영에서는 **빌드 시점**에 박힙니다 — 값을 바꾸면 재빌드입니다.
 */
export const COMING_SOON =
  process.env.NEXT_PUBLIC_COMING_SOON === undefined
    ? true
    : process.env.NEXT_PUBLIC_COMING_SOON !== "0";

/**
 * 🔍 검색 색인을 허용하는가.
 *
 * 🔴 **오픈 스위치와 별개이고, 기본은 거부입니다.** `COMING_SOON` 을 끄는 첫 이유는
 *    테스터에게 공유하는 것인데(2026-09-17 · 티켓월드), 그 순간 검색에 잡히면 반쯤
 *    준비된 화면이 정식 오픈 뒤에도 한동안 결과에 남습니다 — 준비 중 화면을
 *    `noindex` 로 막던 이유와 같습니다. 종전에는 `COMING_SOON` 하나가 둘을 겸해
 *    «테스트 공개» 와 «검색 노출» 을 가를 수 없었습니다.
 *    `layout.tsx` 가 이 값이 `false` 인 동안 `noindex` 를 보냅니다.
 *    정식 오픈 때 service 브랜치에서 `true` 로 바꿉니다. ⚠️ `COMING_SOON` 이 켜져
 *    있으면 이 값과 무관하게 `noindex` 입니다.
 */
export const SEARCH_INDEX = false as boolean;

/*
 * ─────────────────────────────────────────────────────────────────────────────
 * 아래는 몰 화면이 실제로 문을 열면서 늘어난 값들입니다 (2026-09-07).
 * 🔴 위와 같은 규칙입니다 — 화면은 여기서 **읽기만** 합니다.
 * ─────────────────────────────────────────────────────────────────────────────
 */

/**
 * 문의 전화.
 *
 * 🔑 **주 사용자가 30~70대 남성**이라 전화 접점이 실제로 쓰입니다(오너 결정 #4).
 *    이메일만 두면 "물어볼 데가 없는 화면" 이 됩니다.
 *
 * ⚠️ **빈 문자열이면 화면에서 아예 숨깁니다.** 자리표시 번호를 그대로 두면
 *    엉뚱한 사람에게 전화가 갑니다 — 없는 편이 낫습니다.
 */
export const CONTACT_PHONE = "" as string;

/**
 * 홈 메인 비주얼. 영상·포스터·문구는 납품처 설정으로만 교체합니다.
 * imageSrc 두 벌은 첫 프레임 포스터이며 영상 오류/동작 줄이기에서도 사용합니다.
 * 영상 주소가 비어 있으면 포스터·첫 문구만 표시합니다.
 * public의 기본 영상은 코어 자산입니다. 납품처 전용 자산은 담당자가 제공한 정적 HTTPS
 * URL을 아래에 설정합니다. service 브랜치에서 코어 파일/미디어를 덮어쓰지 않습니다.
 */
export const MALL_HERO = {
  imageSrc: "/images/hero-video-poster.jpg",
  imageSrcMobile: "/images/hero-video-poster-mobile.jpg",
  videoSrc: "/videos/hero-loop.mp4",
  videoSrcMobile: "/videos/hero-loop-mobile.mp4",
  videoPositionMobile: "center 15%",
  // 영상 시작 기준 초(at), 오름차순. 루프/탐색/복귀도 같은 장면의 문구를 표시합니다.
  slogans: [
    { at: 0, text: "오늘 쓸 것도,\n선물할 것도." },
    { at: 8, text: "사고 싶었던 게\n하나쯤은 있으니까." },
    { at: 14, text: "이번엔\n제 것도 하나." },
    { at: 22, text: "쿠폰은 미리,\n계산은 가볍게." },
  ],
  sloganTransitionMs: 600,
  eyebrow: "모바일 상품권",
  title: "상품권 구매\n결제 후 핀번호 발급",
  description: "핀번호 · 바코드 확인은 보관함에서",
  mobileDescription: "결제와 동시에 핀번호가 발급됩니다.",
  guestPromptTitle: "처음 오셨나요?",
  guestPromptDescription: "이메일과 비밀번호로 가입합니다",
  guestNotice: "로그인 후 구매 가능",
  /**
   * PC 영상 오른쪽 세로 빠른 메뉴 3개의 문구·장식 사진 (2026-09-14 오너 결정). 이동 경로는
   * 코어 화면(`/products` · `/orders` · `/coupons`). 사진은 피사체 오른쪽의 2:1 WebP 권장입니다.
   * 모바일에는 하단 탭이 같은 곳으로 가므로 이 메뉴를 그리지 않습니다.
   */
  quickMenu: {
    products: { label: "상품권 구매", description: "모바일 상품권 목록", tooltip: "판매 중인 상품권 목록으로 갑니다.", imageSrc: "/images/quick-menu/products.webp" },
    orders: { label: "주문내역", description: "결제 · 발급 상태 확인", tooltip: "주문 내역과 발급 상태를 봅니다.", imageSrc: "/images/quick-menu/orders.webp" },
    coupons: { label: "내 쿠폰", description: "핀번호 · 바코드 확인", tooltip: "내가 산 상품권의 핀번호를 봅니다.", imageSrc: "/images/quick-menu/coupons.webp" },
  },
  guestActionLabel: "회원 가입",
  memberActionLabel: "내 쿠폰",
  loginActionLabel: "로그인",
  imageAlt: "밝은 매장에서 쇼핑을 즐기는 일상",
  guestPromptTooltip: "회원 가입 화면으로 갑니다.",
  memberPromptTooltip: "내 쿠폰을 확인합니다.",
} as const;

/**
 * 🔴 **취소 규칙 — 화면마다 같은 문장을 씁니다** (`docs/design/mall-redesign/README.md` §7).
 *    표현이 화면마다 달라지면 사용자가 "규칙이 다르다" 고 읽습니다. 새 문구를
 *    떠올리지 말고 이 상수를 그대로 가져다 쓰세요.
 */
export const COUPON_CANCEL_RULE =
  "미사용 상품권만 취소됩니다. 취소 시 결제 수단으로 환불되며 보관함에서 삭제됩니다.";

/** 상품 상세의 납품처별 표시 문구. */
export const PRODUCT_DETAIL_UI = {
  pageTitle: "상품 상세",
  immediateBadge: "결제 즉시 발급",
  informationTitle: "상품 안내",
  totalLabel: "결제 금액",
  buyLabel: "구매하기",
  soldOutLabel: "품절",
  soldOutMessage: "현재 품절된 상품입니다.",
  soldOutTooltip: "품절되어 지금은 구매할 수 없습니다.",
  buyTooltip: "결제 내용을 한 번 더 확인하는 화면으로 갑니다. 아직 결제되지 않습니다.",
  purchaseNotice: `결제가 끝나면 핀번호가 내 쿠폰에 저장됩니다. ${COUPON_CANCEL_RULE}`,
} as const;

/**
 * 고객센터 운영 시간. 전화번호·이메일과 함께 납품처 설정에서 관리합니다.
 *
 * 🔑 **점심시간이 필요 없는 납품처는 `lunchBreak` 를 빈 문자열로 두세요** — 그
 *    줄 자체가 그려지지 않습니다. 화면(`HomeSupport.tsx`)에 값을 하드코딩하지 말고
 *    여기서 관리합니다.
 */
export const SUPPORT_HOURS = {
  weekday: "평일 09:00 – 18:00",
  lunchBreak: "점심 12:00 – 13:00",
  closed: "주말 · 공휴일 휴무",
} as const;

/** 정적 고객 안내. 공지 게시판·문의 접수 기능이 아니며, 납품처별 문구는 여기서 관리합니다. */
export const MALL_FAQ = [
  {
    question: "핀번호는 어디에서 확인하나요?",
    answer: "보관함에서 상품권을 선택하면 핀번호와 바코드를 확인할 수 있습니다.",
    href: "/coupons",
    linkLabel: "보관함",
  },
  {
    question: "취소 신청은 어디에서 하나요?",
    answer: "주문내역에서 주문을 선택한 뒤 취소 가능한 쿠폰을 선택해 신청합니다. 환불은 결제한 수단으로 처리됩니다.",
    href: "/orders",
    linkLabel: "주문내역",
  },
  {
    question: "결제했는데 상품권이 안 보여요.",
    answer: "주문내역에서 발급 상태를 확인해 주세요. 발급 지연 상태라면 다시 결제하지 말고 주문번호와 함께 고객센터로 문의해 주세요.",
    href: "/orders",
    linkLabel: "발급 상태 확인",
  },
  {
    question: "상품권은 어디에서 사용할 수 있나요?",
    answer: "상품 상세에 등록된 상품 안내를 확인해 주세요. 사용처·이용조건이 없다면 구매 전 발행처 또는 고객센터에 확인해 주세요.",
    href: "/products",
    linkLabel: "상품권 목록",
  },
] as const;

export const MALL_USAGE_GUIDE = [
  { title: "구매 전 확인", body: "액면가·발행처·유효기간과 등록된 상품 안내를 확인해 주세요. 사용처·이용조건이 기재되지 않은 상품은 구매 전 확인이 필요합니다." },
  { title: "핀번호 보관", body: "발급된 상품권은 보관함에서 확인할 수 있습니다. 핀번호와 바코드를 다른 사람에게 전달하지 않도록 주의해 주세요." },
  { title: "취소·환불", body: "취소 가능 여부는 주문내역에서 확인할 수 있습니다. 사용 완료·기간 만료 상품권은 취소할 수 없습니다." },
] as const;

/*
 * 🔄 **약관 주소(`TERMS_URL` · `PRIVACY_URL`)는 없어졌습니다** (2026-09-23 · `docs/base/11` §2-3).
 *    약관 본문은 관리자가 인트라넷 에디터로 쓰고(`legal_documents`), 몰은 `GET /legal/{kind}` 를
 *    받아 **시트**로 엽니다(`_ui/LegalSheet.tsx`). 본문이 있는 약관만 `useSite().legal` 에 오고,
 *    푸터·가입 화면은 **있는 것만** 링크를 그립니다 — 외부 주소 전제가 사라졌습니다.
 */

/**
 * 값이 실제로 채워졌는가 — 빈 문자열과 `CHANGE_ME` 자리표시는 «없음» 입니다.
 * 🔴 화면은 없는 항목을 **그리지 않습니다** (푸터·로그인 왼쪽 패널). 자리표시가 그대로
 *    공개 화면에 나가는 것이 이 판정이 막는 사고입니다.
 */
export const isFilled = (value: string): boolean =>
  value.trim() !== "" && !value.includes("CHANGE_ME");

/*
 * 🔴 정식 오픈 빌드에는 푸터와 가입 화면에 내보낼 실제 법정 정보가 모두 있어야 합니다.
 *    ⚠️ 약관 본문은 DB 라 빌드가 확인할 수 없습니다 — 정식 오픈 전에 인트라넷 «약관» 에서
 *    이용약관·개인정보 처리방침이 채워졌는지 사람이 봅니다. `escrowProvider` 는 PG 계약
 *    전이라(`fake`) 아직 필수에 넣지 않았습니다.
 *
 * 🔑 **테스트 공유(`COMING_SOON=false` · `SEARCH_INDEX=false`)는 예외입니다** (2026-09-17 ·
 *    오너 결정, 티켓월드). 법인 정보를 아직 받지 못한 채 테스터에게 화면을 열어야
 *    하는 단계가 실제로 있고, 그때 결제는 `PAYMENT_PROVIDER=fake` 가 운영에서 막습니다.
 *    없는 항목은 화면이 숨깁니다(`isFilled`). 색인을 허용하는 순간이 «정식 오픈» 이고,
 *    그때는 이 가드가 빌드를 막습니다.
 */
if (process.env.NODE_ENV === "production" && !COMING_SOON && SEARCH_INDEX) {
  const required = {
    contactEmail: CONTACT_EMAIL,
    companyName: COMPANY.name,
    companyNameEn: COMPANY.nameEn,
    ceo: COMPANY.ceo,
    bizNo: COMPANY.bizNo,
    mailOrderNo: COMPANY.mailOrderNo,
    address: COMPANY.address,
    privacyOfficer: COMPANY.privacyOfficer,
    hostingProvider: COMPANY.hostingProvider,
  };
  const invalid = Object.entries(required)
    .filter(([, value]) => !value || value.includes("CHANGE_ME"))
    .map(([key]) => key);

  if (invalid.length > 0) {
    throw new Error(`몰을 공개하려면 필수 공개 정보를 채워야 합니다: ${invalid.join(", ")}`);
  }
}
